import {
  RichTextContent,
  type TextContent,
} from "@prismicio/types-internal/lib/content"
import type { Text } from "@prismicio/types-internal/lib/customtypes"
import { type ReactNode, useMemo, useRef } from "react"

import { RichTextField } from "./RichTextField"
import { getFieldLabel } from "./utils"

interface Props {
  content: TextContent | undefined
  field: Text
  id: string
  onContentChange: (content: TextContent | undefined) => void
  readOnly: boolean
  endAdornment?: ReactNode
  placeholder?: string
}

export function KeyTextField(props: Props) {
  const { content, field, id, onContentChange, readOnly, placeholder } = props

  const lastLocalUpdate = useRef<LocalUpdate>()

  const contentValue = content?.value

  const richTextContent = useMemo(() => {
    // The content is still the same so try to reuse our last seen richtext content to keep the RTF stable.
    if (lastLocalUpdate.current?.plainText?.value === contentValue)
      return lastLocalUpdate.current?.richText

    return plainTextToRichText(contentValue)
  }, [contentValue])

  return (
    <RichTextField
      id={id}
      icon="notes"
      field={{
        type: "StructuredText",
        config: {
          label: getFieldLabel(field),
          placeholder,
          multi: "paragraph",
          useAsTitle: field.config?.useAsTitle,
        },
      }}
      content={richTextContent}
      onContentChange={(richText) => {
        const plainText = richTextToPlainText(richText)

        lastLocalUpdate.current = {
          richText,
          plainText,
        }

        onContentChange(plainText)
      }}
      readOnly={readOnly}
    />
  )
}

type LocalUpdate = {
  richText?: RichTextContent
  plainText?: TextContent
}

function plainTextToRichText(plainText?: string): RichTextContent | undefined {
  if (!plainText) return undefined

  return {
    __TYPE__: "StructuredTextContent",
    value: plainText
      .split("\n")
      .map((line) => ({ type: "paragraph", content: { text: line } })),
  }
}

function richTextToPlainText(
  richText?: RichTextContent,
): TextContent | undefined {
  const value = richText?.value
    .map((block) => ("content" in block ? block.content.text : ""))
    .join("\n")

  if (!value) return undefined

  return { __TYPE__: "FieldContent", type: "Text", value }
}
